import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { DxCheckBoxComponent } from 'devextreme-angular/ui/check-box';
import { DxRadioGroupComponent } from 'devextreme-angular/ui/radio-group';
import { DxTextAreaComponent } from 'devextreme-angular/ui/text-area';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxValidatorComponent } from 'devextreme-angular/ui/validator';
import { ValidationRule } from 'devextreme/common';
import { confirm } from 'devextreme/ui/dialog';
import validationEngine from 'devextreme/ui/validation_engine';
import { ThomasPrompt, ThomasPromptApps } from '../../dtos/training-assistant-chat';
import { ThomasAppConfig } from './thomas-app.config';

const PROMPT_MAX_LENGTH = 1000;
const TITLE_MAX_LENGTH = 100;
// ids are the API's ThomasApp names
const THOMAS_APPS = [
  { id: 'Core', name: 'Core' }, { id: 'Tracking', name: 'Tracking' }, { id: 'Costing', name: 'Costing' },
  { id: 'Estimating', name: 'Estimating' }, { id: 'Config', name: 'Config' }, { id: 'Hub', name: 'Hub' },
  { id: 'AdminPortal', name: 'Admin Portal' }
];

interface PromptEdit {
  id: number | null;
  description: string;
  text: string;
  isCompany: boolean;
  apps: string[];
  // where the prompt lives now; the route is chosen by it, and isCompany may move it
  wasCompany: boolean;
}

// Saved prompts panel over the conversation; only super users and company admins change company prompts
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- shared Thomas markup uses the js- prefix in every app
  selector: 'js-thomas-prompts',
  templateUrl: './thomas-prompts.component.html',
  imports: [DxTextBoxComponent, DxTextAreaComponent, DxRadioGroupComponent, DxCheckBoxComponent, DxValidatorComponent]
})
export class ThomasPromptsComponent implements OnInit, OnDestroy {
  // null opens the list; a string (even '') opens a new prompt with that text
  @Input() draft: string | null = null;
  // Thomas's answer to that message, which shows the apps it works in
  @Input() draftResponseId: string | null = null;
  @Output() send = new EventEmitter<string>();
  @Output() changed = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  readonly promptMaxLength = PROMPT_MAX_LENGTH;
  readonly titleMaxLength = TITLE_MAX_LENGTH;
  readonly validationGroup = 'thomasPrompt';
  readonly canManageCompany: boolean;
  readonly appId: string;
  readonly scopes = [{ value: false, text: 'Only me' }, { value: true, text: 'Everyone in my company' }];
  readonly titleRules: ValidationRule[] = [
    { type: 'required', message: 'Enter a title' },
    { type: 'stringLength', max: TITLE_MAX_LENGTH, message: 'Title cannot exceed 100 characters' }
  ];
  readonly textRules: ValidationRule[] = [
    { type: 'required', message: 'Enter the prompt' },
    { type: 'stringLength', max: PROMPT_MAX_LENGTH, message: 'Prompt cannot exceed 1000 characters' }
  ];

  rows: ThomasPrompt[] = [];
  loading = true;
  editing: PromptEdit | null = null;
  saving = false;
  savedId: number | null = null;
  checking = false;
  checkNote: string | null = null;
  // bumped by the user's own edits, so a check that returns after them is ignored
  private revision = 0;
  private savedTimer = 0;

  private app = inject(ThomasAppConfig);

  constructor() {
    const user = this.app.auth.getCurrentUser();
    this.canManageCompany = !!user?.isSuperUser || (this.app.auth.isAdminUser() && !!user?.canUseAi);
    this.appId = this.app.appId;
  }

  ngOnInit() {
    if (this.draft !== null) {
      this.add(this.draft);
      if (this.draftResponseId) {
        this.checkSavedMessage(this.draftResponseId);
      }
      this.draft = null;
    }
    this.load();
  }

  readonly canChange = (row: ThomasPrompt) => !row.isCompany || this.canManageCompany;

  // apps the user can open, plus any the prompt already shows in; read each time, as permissions may still be loading
  appOptions(): { id: string, name: string }[] {
    const openable = this.app.openableApps();
    return THOMAS_APPS.filter(app => openable.includes(app.id) || this.hasApp(app.id));
  }

  async checkApps() {
    const prompt = this.editing;
    if (!prompt || this.checking) {
      return;
    }
    if (!prompt.text?.trim()) {
      this.checkNote = 'Enter the prompt first.';
      return;
    }
    this.checking = true;
    const revision = this.revision;
    try {
      const result = await this.call(this.app.transport.checkPromptApps({ text: prompt.text }));
      if (this.editing === prompt && this.revision === revision) {
        this.applyCheck(prompt, result);
      }
    } catch {
      // the error is shown
    } finally {
      // add and edit reset it, so an older check must not clear a newer one's
      if (this.editing === prompt) {
        this.checking = false;
      }
    }
  }

  // a saved message's answer is logged, so usually no model call is needed; the user can still check by hand
  private checkSavedMessage(responseId: string) {
    const prompt = this.editing;
    const revision = this.revision;
    this.checking = true;
    firstValueFrom(this.app.transport.checkPromptApps({ responseId }))
      .then(result => this.editing === prompt && this.revision === revision && prompt && this.applyCheck(prompt, result))
      .catch(() => { })
      .finally(() => {
        if (this.editing === prompt) {
          this.checking = false;
        }
      });
  }

  // ticks the apps it works in; the user can still change them
  private applyCheck(prompt: PromptEdit, result: ThomasPromptApps) {
    const openable = this.app.openableApps();
    const usable = result.apps.filter(id => openable.includes(id));
    const names = this.appsLabel(result.apps);
    if (!result.apps.length) {
      this.checkNote = result.topics.length ? 'No single app can answer all of this.' : "Couldn't tell where this works. Tick the apps yourself.";
    } else if (!usable.length) {
      this.checkNote = `It works in ${names}, which you can't open.`;
    } else {
      prompt.apps = usable;
      this.checkNote = result.topics.length
        ? `Thomas would answer this with ${result.topics.join(' and ')}, so it works in ${names}.`
        : 'Thomas would answer this from the help docs, so it works in every app.';
    }
  }

  hasApp(id: string): boolean {
    return !!this.editing?.apps.includes(id);
  }

  setApp(id: string, on: boolean) {
    if (this.editing && on !== this.hasApp(id)) {
      this.editing.apps = on ? [...this.editing.apps, id] : this.editing.apps.filter(app => app !== id);
      this.revision++;
    }
  }

  textEdited(event: { event?: Event }) {
    if (event.event) {
      this.checkNote = null;
      this.revision++;
    }
  }

  appsLabel(ids: string[]): string {
    return ids.length === THOMAS_APPS.length ? 'All apps'
      : THOMAS_APPS.filter(app => ids.includes(app.id)).map(app => app.name).join(', ');
  }

  add(text = '') {
    this.checkNote = null;
    this.checking = false;
    this.revision++;
    this.editing = { id: null, description: text.split('\n')[0].trim().slice(0, 60), text, isCompany: false, apps: [this.appId], wasCompany: false };
  }

  edit(row: ThomasPrompt) {
    this.checkNote = null;
    this.checking = false;
    this.revision++;
    const { id, description, text, isCompany, apps } = row;
    this.editing = { id, description, text, isCompany: !!isCompany, apps: [...apps ?? []], wasCompany: !!isCompany };
  }

  cancel() {
    if (!this.saving) {
      this.editing = null;
    }
  }

  // the docked panel closes Thomas on Escape, so the panel's own Escape must not reach it
  @HostListener('keydown.escape', ['$event'])
  onEscape(event: Event) {
    event.stopPropagation();
    if (this.editing) {
      this.cancel();
    } else {
      this.closed.emit();
    }
  }

  focusEditor(e: { component?: { focus: () => void } }) {
    setTimeout(() => e.component?.focus());
  }

  async save() {
    const prompt = this.editing;
    if (!prompt || this.saving || !prompt.apps.length || !validationEngine.validateGroup(this.validationGroup).isValid) {
      return;
    }
    this.saving = true;
    try {
      const { id, description, text, isCompany, apps, wasCompany } = prompt;
      let saved: ThomasPrompt;
      if (id === null) {
        saved = await this.call(this.app.transport.addPrompt({ description, text, isCompany, apps }, isCompany));
      } else {
        // send only what changed, to the route for where the prompt lives now
        const original = this.rows.find(row => row.id === id);
        const changes: Partial<ThomasPrompt> = {};
        if (description !== original?.description) {
          changes.description = description;
        }
        if (text !== original?.text) {
          changes.text = text;
        }
        if (isCompany !== original?.isCompany) {
          changes.isCompany = isCompany;
        }
        if (this.appsLabel(apps) !== this.appsLabel(original?.apps ?? [])) {
          changes.apps = apps;
        }
        if (!Object.keys(changes).length) {
          this.editing = null;
          return;
        }
        saved = await this.call(this.app.transport.updatePrompt(id, changes, wasCompany));
      }
      this.editing = null;
      this.markSaved(saved?.id ?? id);
      this.changed.emit();
      await this.load();
    } catch {
      // the error is shown; the editor stays open with the user's text
    } finally {
      this.saving = false;
    }
  }

  async remove() {
    const prompt = this.editing;
    if (!prompt || prompt.id === null || this.saving) {
      return;
    }
    this.saving = true;
    try {
      if (!await confirm('Delete this saved prompt?', 'Delete prompt')) {
        return;
      }
      await this.call(this.app.transport.deletePrompt(prompt.id, prompt.wasCompany));
      this.editing = null;
      this.changed.emit();
      await this.load();
    } catch {
      // the error is shown
    } finally {
      this.saving = false;
    }
  }

  private load(): Promise<void> {
    return this.call(this.app.transport.getPrompts(true))
      .then(rows => { this.rows = rows; })
      .catch(() => { })
      .finally(() => this.loading = false);
  }

  private markSaved(id: number | null) {
    this.savedId = id;
    clearTimeout(this.savedTimer);
    this.savedTimer = window.setTimeout(() => this.savedId = null, 2000);
  }

  private call<T>(request: Observable<T>): Promise<T> {
    return firstValueFrom(request).catch(err => {
      this.app.notifications.showWarning(this.app.global.returnError(err));
      throw err;
    });
  }

  ngOnDestroy() {
    clearTimeout(this.savedTimer);
  }
}
